// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import styles from './PinToCompare.module.css'
import { useDeclaredContext } from 'lib/owner-notes/declared'
import { MAX_PINS, useLabCompare, type LabTarget } from 'lib/owner-notes/lab-compare'
import { useOwnerTrack } from 'lib/owner-notes/analytics'
import { sameProduct } from 'lib/owner-notes/deltas'

/** What a click on the button does, given the mode and whether this pick is already pinned.
 *  Pure so both surfaces' behaviour can be read — and tested — without a DOM. */
export type PinClick = 'pin' | 'open' | 'unpin'

export function pinClick(mode: 'open' | 'toggle', pinned: boolean): PinClick {
  if (!pinned) {
    return 'pin'
  }
  return mode === 'toggle' ? 'unpin' : 'open'
}

/** Where the pin button sits — the `location` of its `*_owner_pin` event (2026-10-17): `card`
 *  the category grid's card, `section` a guide's in-depth pick card, `grid` a guide's "Our
 *  picks" card, `roster` a guide's Quick Picks row. The drawer's own actions report `drawer`. Before this every seat was one row. */
export type PinLocation = 'card' | 'section' | 'grid' | 'roster'

/** The event's params for a click: `slots_used` is the pinned count AFTER the action. */
export function pinEvent(
  click: PinClick,
  location: PinLocation,
  pinnedBefore: number,
  ok = true
): { action: PinClick | 'blocked'; slots_used: number; location: PinLocation } {
  if (click === 'unpin') {
    return { action: 'unpin', slots_used: pinnedBefore - 1, location }
  }
  if (click === 'open') {
    return { action: 'open', slots_used: pinnedBefore, location }
  }
  return { action: ok ? 'pin' : 'blocked', slots_used: pinnedBefore + (ok ? 1 : 0), location }
}

/** The button's words — they must say what the NEXT click will do. */
/** A pin that cannot land: the set is full and this pick is not in it. The tile's `onBlocked`
 *  takes over here (the sheet's own Compare, where Unpin lives) instead of the provider's
 *  refusal, which opens the drawer. */
export function pinBlocked(pinned: boolean, count: number, max = MAX_PINS): boolean {
  return !pinned && count >= max
}

export function pinLabel(mode: 'open' | 'toggle', pinned: boolean): string {
  if (!pinned) {
    return 'Pin to compare'
  }
  return mode === 'toggle' ? 'Pinned · Unpin' : 'Pinned · Open'
}

interface PinToCompareProps extends LabTarget {
  /**
   * False: pin without a declared product. A guide's pick offers the comparison before the
   * reader has said what they own — the drawer then overlays the pinned picks on each other,
   * with no "yours" curve. The category page leaves this true: there, a pin only ever means
   * "against my pair", and the grid's cards are already a comparison of their own.
   */
  requireDeclared?: boolean
  /**
   * What a second click does. `open` (the default, the category page): the button opens the
   * drawer and unpinning lives in its rows — Directive `4459eb4a`, so a thumb on a grid of
   * cards cannot drop a comparison it was scrolling past. `toggle` (the guide): the same
   * button unpins, because a pick's block is a place the reader is reading, not scrolling
   * through, and the pin is the only control there (Guillaume, 2026-09-15).
   */
  mode?: 'open' | 'toggle'
  /** The seat, for the event's `location`; the category card when absent. */
  location?: PinLocation
  /** `pill` (the default): the small pill on a card's top row or photo. `tile`: one slot of a
   *  guide pick's action row (2026-10-17) — the row's height, the count under the label.
   *  `icon`: the same slot on the "Our picks" grid card, where three words do not fit — the
   *  pin glyph and the count, the label for screen readers. */
  variant?: 'pill' | 'tile' | 'icon'
  /** The set is full and this pick is not in it: called INSTEAD of asking the provider, which
   *  would open the drawer — under the pick sheet's modal on a phone (2026-10-19). The sheet
   *  passes its own Compare segment, where Unpin lives; the desktop panel needs nothing (the
   *  provider's refusal already lands it on Compare). */
  onBlocked?: () => void
}

/**
 * "Pin to compare" on the card's top row (Guillaume, 2026-09-09: "could go on the right side
 * of the card top"), opposite the category eyebrow — the same spot on every card, clear of
 * the badge shield that owns the image's corner. Renders only where the drawer can draw
 * this card: inside the category page's provider, with a declared product, for a card the
 * lab measured. Pinned → "Pinned · Open" opens the drawer and unpin lives in the drawer rows;
 * in `mode="toggle"` (the guide) the same button unpins instead, and the last one out clears
 * the comparison.
 */
const PinToCompare: React.VFC<PinToCompareProps> = ({
  nodeId,
  slug,
  name,
  pdpHref,
  clearbuyProductId,
  requireDeclared = true,
  mode = 'open',
  location = 'card',
  variant = 'pill',
  onBlocked,
}) => {
  const labCompare = useLabCompare()
  const declared = useDeclaredContext()?.declared ?? null
  const trackOwner = useOwnerTrack({ nodeId, name })
  // A pin opens the drawer on desktop, where it is a side panel; on a phone it only docks the
  // collapsed bar, so the list stays under the thumb (2026-10-17: the sheet covered the page
  // on the first pin). Before the breakpoints are known, the server's answer — desktop.
  const responsive = ResponsiveContext.useOptionalContainer()
  const openOnPin = responsive?.ready !== true || responsive.lg
  if (
    !labCompare ||
    (requireDeclared && !declared) ||
    clearbuyProductId === null ||
    (declared && sameProduct({ nodeId: declared.nodeId, slug: declared.slug }, { nodeId, slug }))
  ) {
    return null
  }
  const pinned = labCompare.isPinned(nodeId)
  const click = pinClick(mode, pinned)
  const pinnedTitle =
    click === 'unpin'
      ? 'Take this one out of the comparison'
      : 'Open the comparison — unpin from there'
  const target: LabTarget = { nodeId, slug, name, pdpHref, clearbuyProductId }
  return (
    <button
      type="button"
      className={clsx(styles.pin, {
        [styles.pinOn]: pinned,
        [styles.tile]: variant !== 'pill',
        [styles.iconOnly]: variant === 'icon',
      })}
      aria-pressed={pinned}
      title={
        pinned
          ? pinnedTitle
          : "Overlay this card's lab curves with your pair and up to two more cards"
      }
      onClick={() => {
        const before = labCompare.pinned.length
        if (click === 'unpin') {
          // The last one out leaves no pins, and the drawer renders nothing at zero — the
          // provider's `unpin` needs no help to clear the comparison.
          labCompare.unpin(nodeId)
          trackOwner('pin', pinEvent(click, location, before))
          return
        }
        if (click === 'open') {
          labCompare.openDrawer()
          trackOwner('pin', pinEvent(click, location, before))
          return
        }
        if (onBlocked && pinBlocked(pinned, before, labCompare.maxPins)) {
          onBlocked()
          trackOwner('pin', pinEvent(click, location, before, false))
          return
        }
        const ok = labCompare.pin(target, openOnPin)
        trackOwner('pin', pinEvent(click, location, before, ok))
      }}
    >
      <svg
        className={styles.icon}
        viewBox="0 0 12 12"
        width="11"
        height="11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M7.5 1.5l3 3-1.2.4-2 2 .3 2.3L6 8.6 3.4 11 1 8.6l2.4-2.6L1.6 4.4l2.3.3 2-2z" />
      </svg>
      <span className={styles.label}>{pinLabel(mode, pinned)}</span>
      {variant !== 'pill' && (
        <small
          className={styles.tileSub}
        >{`${labCompare.pinned.length} of ${labCompare.maxPins}`}</small>
      )}
    </button>
  )
}

export default PinToCompare
